/* eslint-disable react/no-array-index-key, @typescript-eslint/no-use-before-define */
// Disabling array-index-key: diagnostic data arrays don't have stable IDs
// Disabling no-use-before-define: main component defined first, helper components follow

import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as WaitingIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect,useMemo, useState } from 'react';

import { useSnackBar } from '../../hooks/useSnackBar';
import { LivePreviewDiagnostics } from '../../types/previewStudio';
import DebugAccordionPanel, { NormalizedMetadata } from './DebugAccordionPanel';

interface LivePreviewDiagnosticPanelProps {
  diagnostics: LivePreviewDiagnostics;
  onSwitchToSimulated: () => void;
  onRetry: () => void;
  metadata?: NormalizedMetadata;
  fireTracking?: boolean;
}

export default function LivePreviewDiagnosticPanel({
  diagnostics,
  onSwitchToSimulated,
  onRetry,
  metadata,
  fireTracking = false,
}: LivePreviewDiagnosticPanelProps) {
  const { showSnackBar } = useSnackBar();

  // Determine if the preview was successful
  // Success = has ad markup OR failure_reason is 'unknown' with all critical checks passing
  const isPreviewSuccessful = useMemo(() => {
    if (diagnostics.technical_details.has_ad_markup) {
      return true;
    }

    const {checks} = diagnostics;
    const allCriticalChecksPassed =
      checks.line_item_in_venice &&
      checks.venice_reachable &&
      checks.preview_compiled_identifiers_present;

    // If failure_reason is 'unknown' and critical checks pass, consider it successful
    if (diagnostics.failure_reason === 'unknown' && allCriticalChecksPassed) {
      return true;
    }

    return false;
  }, [diagnostics]);

  // Default tab: diagnostics
  // Users can switch to "Creative Details" if metadata is available
  const defaultTab = 'diagnostics';

  const [activeTab, setActiveTab] = useState<'diagnostics' | 'debug'>(defaultTab);
  const [expandedSection, setExpandedSection] = useState<string | false>('pipeline');

  useEffect(() => {
    setActiveTab(defaultTab);
    setExpandedSection('pipeline');
  }, [diagnostics.technical_details.diagnostics_id, defaultTab]);

  if (!diagnostics) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Error: No diagnostics data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Tab Switcher */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 1, textTransform: 'none', fontSize: '0.875rem' }
          }}
        >
          <Tab
            label="Diagnostics"
            value="diagnostics"
          />
          <Tab
            label="Creative Details"
            value="debug"
            disabled={!metadata}
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box>
        {activeTab === 'diagnostics' ? (
          <DiagnosticContent
            diagnostics={diagnostics}
            onSwitchToSimulated={onSwitchToSimulated}
            onRetry={onRetry}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            showSnackBar={showSnackBar}
            isPreviewSuccessful={isPreviewSuccessful}
          />
        ) : (
          metadata && (
            <DebugAccordionPanel 
              metadata={metadata} 
              previewId={diagnostics.technical_details.diagnostics_id}
              previewMode="live"
              fireTracking={fireTracking}
            />
          )
        )}
      </Box>
    </Box>
  );
}

// Extracted Diagnostic Content Component
function DiagnosticContent({
  diagnostics,
  onSwitchToSimulated,
  onRetry,
  expandedSection,
  setExpandedSection,
  showSnackBar,
  isPreviewSuccessful,
}: {
  diagnostics: LivePreviewDiagnostics;
  onSwitchToSimulated: () => void;
  onRetry: () => void;
  expandedSection: string | false;
  setExpandedSection: (section: string | false) => void;
  showSnackBar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    tracking?: { event: string; [key: string]: unknown }
  ) => void;
  isPreviewSuccessful: boolean;
}) {
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSnackBar(`${label} copied to clipboard`, 'success', { event: 'preview_diagnostic_copied', label });
  };

  // Get status level icon and color
  const getStatusIcon = (level: string, isSuccess: boolean) => {
    // Override with success styling if preview was successful
    if (isSuccess) {
      return { icon: <CheckCircleIcon />, color: 'success' as const };
    }

    switch (level) {
      case 'action_required':
        return { icon: <ErrorIcon />, color: 'error' as const };
      case 'waiting':
        return { icon: <WaitingIcon />, color: 'warning' as const };
      case 'blocked':
        return { icon: <BlockIcon />, color: 'error' as const };
      case 'info':
      default:
        return { icon: <InfoIcon />, color: 'info' as const };
    }
  };

  // Get check status icon
  const getCheckStatusIcon = (status: boolean | string) => {
    if (status === true || status === 'pass') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    } if (status === 'pending') {
      return <CircularProgress size={16} sx={{ color: 'warning.main' }} />;
    } if (status === 'unknown') {
      return <InfoIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
    } 
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    
  };

  // action handler
  const handleAction = (action: string) => {
    const { campaign_id: campaignId } = diagnostics.technical_details;

    switch (action) {
      case 'retry':
        onRetry();
        break;
      case 'use_simulated':
      case 'republish':
        onSwitchToSimulated();
        break;
      case 'wait':
        showSnackBar('Waiting for sync to complete...', 'info', { event: 'preview_diagnostic_action', action, campaign_id: campaignId });
        break;
      case 'contact_support':
        showSnackBar('Please contact your support team for assistance', 'info', { event: 'preview_diagnostic_action', action, campaign_id: campaignId });
        break;
      case 'fix_targeted_creative':
        if (campaignId) {
          window.open(`/admin/campaigns/${campaignId}/targeted_creatives`, '_blank');
        }
        break;
      case 'check_targeting':
        if (campaignId) {
          window.open(`/admin/campaigns/${campaignId}/edit#targeting`, '_blank');
        }
        break;
      default:
        break;
    }
  };

  // Derive status_level with backward compatibility for old payload shape
  const getStatusLevel = (): 'action_required' | 'waiting' | 'blocked' | 'info' => {
    if (diagnostics.user_facing?.status_level) {
      return diagnostics.user_facing.status_level;
    }
    // Fallback for old format
    if (diagnostics.failure_reason?.includes('venice_unreachable') || diagnostics.failure_category === 'waiting') {
      return 'waiting';
    }
    if (diagnostics.failure_category === 'blocked') {
      return 'blocked';
    }
    return 'action_required';
  };

  const statusInfo = getStatusIcon(getStatusLevel(), isPreviewSuccessful);

  // Calculate sync progress if available
  const syncProgress = React.useMemo(() => {
    const sync = diagnostics.checks.sync_status;
    if (!sync || !sync.time_remaining_minutes) return null;

    const estimated = 10; // Assumes 10 minute total
    const remaining = sync.time_remaining_minutes;
    const progress = Math.max(0, Math.min(100, ((estimated - remaining) / estimated) * 100));

    return {
      progress,
      remaining,
      status: sync.user_details || sync.details || 'Syncing to ad server',
    };
  }, [diagnostics.checks.sync_status]);

  const allActions = isPreviewSuccessful 
    ? [] 
    : (diagnostics.user_facing?.next_steps || diagnostics.suggested_actions || []);
  
  // Find the first simulate action (prefer use_simulated, then republish)
  const simulateAction = allActions.find(a => a.action === 'use_simulated') || 
                         allActions.find(a => a.action === 'republish');
  
  const actions = allActions.filter(action => {
    if (action.action === 'retry') return false;
    if ((action.action === 'use_simulated' || action.action === 'republish') && action !== simulateAction) {
      return false;
    }
    return true;
  });

  const displayTitle = isPreviewSuccessful
    ? 'Live Preview Successful'
    : (diagnostics.user_facing?.title || 'Live Preview Failed');
  const displaySummary = isPreviewSuccessful
    ? 'All system checks passed. Your creative is live and serving correctly.'
    : (diagnostics.user_facing?.summary || diagnostics.primary_message || '');

  return (
    <Box sx={{ p: 2 }}>
      {/* Status Header with Inline Actions */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: `${statusInfo.color}.50`,
          borderLeft: 4,
          borderColor: `${statusInfo.color}.main`,
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ color: `${statusInfo.color}.main`, mt: 0.5 }}>
            {statusInfo.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {displayTitle}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary', 
                mb: 1.5,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {displaySummary}
            </Typography>
            
            {/* Sync Progress (if waiting) */}
            {syncProgress && (
              <Box sx={{ mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {syncProgress.status}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ~{syncProgress.remaining} min remaining
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={syncProgress.progress}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </Box>
            )}
            
            {/* Inline Actions with Tooltips */}
            {actions.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                {actions
                  .filter(action => action.action !== 'wait')
                  .map((action, index) => {
                    const displayLabel = (action.action === 'use_simulated' || action.action === 'republish')
                      ? 'Use Simulated Preview'
                      : action.label;
                    
                    return (
                      <Tooltip 
                        key={index}
                        title={action.description}
                        placement="bottom"
                        arrow
                      >
                        <Button
                          variant={action.is_primary ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleAction(action.action)}
                          sx={{ 
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            minWidth: 'fit-content',
                            px: 2,
                            fontSize: '0.8125rem',
                          }}
                        >
                          {displayLabel}
                        </Button>
                      </Tooltip>
                    );
                  })}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Publishing Pipeline */}
      <Accordion
        expanded={expandedSection === 'pipeline'}
        onChange={(_, isExpanded) => setExpandedSection(isExpanded ? 'pipeline' : false)}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Publishing Pipeline
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PipelineVisualization diagnostics={diagnostics} />
        </AccordionDetails>
      </Accordion>

      {/* System Checks */}
      <Accordion
        expanded={expandedSection === 'checks'}
        onChange={(_, isExpanded) => setExpandedSection(isExpanded ? 'checks' : false)}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            System Checks
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {diagnostics.check_groups && diagnostics.check_groups.length > 0 ? (
              // Filter out "Pipeline Status" group - that info is shown in Publishing Pipeline
              diagnostics.check_groups
                .filter((group) => group.title !== 'Pipeline Status')
                .map((group, groupIndex, filteredGroups) => (
                  <Box key={groupIndex}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                      {group.title}
                    </Typography>
                    <List dense>
                      {group.items && group.items.length > 0 ? (
                        group.items.map((item, itemIndex) => (
                          <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {getCheckStatusIcon(item.status)}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              secondary={item.detail}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: {
                                  fontWeight: item.status === false || item.status === 'fail' ? 600 : 400,
                                },
                              }}
                              secondaryTypographyProps={{
                                variant: 'caption',
                              }}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No checks available
                        </Typography>
                      )}
                    </List>
                    {groupIndex < filteredGroups.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No system checks available
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Technical Details */}
      <Accordion
        expanded={expandedSection === 'technical'}
        onChange={(_, isExpanded) => setExpandedSection(isExpanded ? 'technical' : false)}
        sx={{
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Technical Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TechnicalDetailsSection diagnostics={diagnostics} onCopy={handleCopy} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// Sub-component: Pipeline Visualization
function PipelineVisualization({ diagnostics }: { diagnostics: LivePreviewDiagnostics }) {
  const pipeline = diagnostics.technical_details.dynamo_pipeline;
  const syncStatus = diagnostics.checks.sync_status;
  const timeRemaining = syncStatus?.time_remaining_minutes;

  const stages = [
    {
      key: 'line_item',
      label: 'Creative Sync',
      description: 'Ad creative data',
    },
    {
      key: 'bid_table',
      label: 'Bid Configuration',
      description: 'Bidding rules',
    },
    {
      key: 'campaign_data',
      label: 'Campaign Settings',
      description: 'Targeting & budgets',
    },
  ];

  const getStageStatus = (key: string): { status: string; detail?: string; color: string; icon: JSX.Element } => {
    const stageStatus = pipeline?.[key] || 'unknown';

    switch (stageStatus) {
      case 'pushed':
        return {
          status: 'Synced',
          color: 'success.main',
          icon: <CheckCircleIcon sx={{ fontSize: 20 }} />
        };
      case 'pending':
        return {
          status: 'Syncing...',
          detail: timeRemaining ? `~${timeRemaining} min remaining` : undefined,
          color: 'warning.main',
          icon: <CircularProgress size={16} />
        };
      case 'not_enqueued':
        return {
          status: 'Not Queued',
          detail: 'Republish required',
          color: 'error.main',
          icon: <ErrorIcon sx={{ fontSize: 20 }} />
        };
      default:
        return {
          status: 'Unknown',
          color: 'grey.500',
          icon: <InfoIcon sx={{ fontSize: 20 }} />
        };
    }
  };

  return (
    <Box>
      <Stack spacing={1.5}>
        {stages.map((stage) => {
          const stageInfo = getStageStatus(stage.key);

          return (
            <Paper
              key={stage.key}
              variant="outlined"
              sx={{
                p: 1.5,
                borderLeft: 4,
                borderLeftColor: stageInfo.color,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    color: stageInfo.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 24,
                  }}
                >
                  {stageInfo.icon}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stage.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {stage.description}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    label={stageInfo.status}
                    size="small"
                    sx={{
                      bgcolor: stageInfo.color,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                  {stageInfo.detail && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      {stageInfo.detail}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

// Sub-component: Bid Response Section
function BidResponseSection({ bidResponse }: { bidResponse: any }) {
  const { showSnackBar } = useSnackBar();

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackBar('Copied to clipboard', 'success', { event: 'preview_diagnostic_copied' });
  };

  // Handle case where bid_response is null/undefined (bid request was not attempted)
  if (!bidResponse) {
    return (
      <Alert severity="info">
        <AlertTitle>Bid Request Not Attempted</AlertTitle>
        <Typography variant="body2">
          Preview failed before reaching the ad server. See System Checks above.
        </Typography>
      </Alert>
    );
  }

  // Check if raw response data is available
  const hasRawResponse = bidResponse.raw_response !== null && bidResponse.raw_response !== undefined;

  // Helper to render raw response section
  const renderRawResponse = () => {
    if (!hasRawResponse) {
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Raw Response
          </Typography>
          <Alert severity="info" sx={{ py: 0.5 }}>
            <Typography variant="caption">No raw response data available</Typography>
          </Alert>
        </Box>
      );
    }

    const rawResponseStr = JSON.stringify(bidResponse.raw_response, null, 2);

    return (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Raw Response
          </Typography>
          <Tooltip title="Copy JSON">
            <IconButton
              size="small"
              onClick={() => handleCopy(rawResponseStr)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: 'grey.50',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {rawResponseStr}
          </pre>
        </Paper>
      </Box>
    );
  };

  // Render error states with raw response still visible
  if (!bidResponse.response_present) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          <AlertTitle>No Bid Response</AlertTitle>
          <Typography variant="body2">
            No bid response was received from the ad server. The request may have timed out or failed.
          </Typography>
        </Alert>
        {renderRawResponse()}
      </Stack>
    );
  }

  if (bidResponse.no_bid_reason !== undefined) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          <AlertTitle>No Bid Returned</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The ad server responded but did not return a bid.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Reason:</strong> {bidResponse.no_bid_reason_description || 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            Code: {bidResponse.no_bid_reason}
          </Typography>
        </Alert>
        {renderRawResponse()}
      </Stack>
    );
  }

  if (bidResponse.error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          <AlertTitle>Bid Response Error</AlertTitle>
          <Typography variant="body2">{bidResponse.error}</Typography>
        </Alert>
        {renderRawResponse()}
      </Stack>
    );
  }

  // Success state - show full details
  return (
    <Stack spacing={2}>
      {/* Summary */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Response Summary
        </Typography>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Has Bid:
            </Typography>
            <Chip
              label={bidResponse.has_bid ? 'Yes' : 'No'}
              size="small"
              color={bidResponse.has_bid ? 'success' : 'error'}
            />
          </Box>
          {bidResponse.bid_price !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Bid Price:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                ${bidResponse.bid_price.toFixed(4)}
              </Typography>
            </Box>
          )}
          {bidResponse.has_adm && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Ad Markup Length:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {bidResponse.adm_length} characters
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Ad Markup Preview */}
      {bidResponse.adm_preview && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Ad Markup Preview
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'grey.50',
              maxHeight: 150,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                wordBreak: 'break-all',
              }}
            >
              {bidResponse.adm_preview}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Raw Response */}
      {renderRawResponse()}
    </Stack>
  );
}

// Extracted outside to avoid react/no-unstable-nested-components
function DetailRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: any;
  onCopy: (text: string, label: string) => void;
}) {
  if (value === null || value === undefined) return null;

  const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}:
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 500,
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayValue}
        </Typography>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={() => onCopy(displayValue, label)}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

// Sub-component: Technical Details Section
function TechnicalDetailsSection({
  diagnostics,
  onCopy,
}: {
  diagnostics: LivePreviewDiagnostics;
  onCopy: (text: string, label: string) => void;
}) {
  const details = diagnostics.technical_details;
  const [debugMetadataExpanded, setDebugMetadataExpanded] = useState(false);
  const [bidResponseExpanded, setBidResponseExpanded] = useState(false);

  // Helper function to format data source labels
  const formatDataSource = (source: string | undefined): string => {
    if (!source) return 'Unknown';
    
    const labels: Record<string, string> = {
      'get_item': 'DynamoDB (Direct)',
      'query': 'DynamoDB (Search)',
      'dynamo_pipeline': 'Pipeline Status',
      'sidekiq_scheduled_set': 'Job Queue',
      'campaign_updated_at': 'Estimated',
    };
    
    return labels[source] || source;
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Diagnostic Session
        </Typography>
        <DetailRow label="Diagnostic ID" value={details.diagnostics_id} onCopy={onCopy} />
        <DetailRow label="Generated At" value={details.generated_at} onCopy={onCopy} />
        <DetailRow label="Environment" value={details.stage} onCopy={onCopy} />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Identifiers
        </Typography>
        <DetailRow label="Campaign ID" value={details.campaign_id} onCopy={onCopy} />
        <DetailRow label="Creative ID" value={details.creative_id} onCopy={onCopy} />
        <DetailRow label="Line Item ID" value={details.line_item_id} onCopy={onCopy} />
        <DetailRow label="Line Item Key Used" value={details.line_item_key_used} onCopy={onCopy} />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Version Information
        </Typography>
        <DetailRow label="Publishable Version" value={details.campaign_publishable_version} onCopy={onCopy} />
        <DetailRow label="Last Published Version" value={details.campaign_last_published_version} onCopy={onCopy} />
        <DetailRow label="Venice Published Version" value={details.tiles_published_campaign_version} onCopy={onCopy} />
        <DetailRow label="DynamoDB Version Used" value={details.dynamo_version_used} onCopy={onCopy} />
      </Box>

      {/* Bid Response - Always show when diagnostics exist */}
      <Divider />
      <Accordion
        expanded={bidResponseExpanded}
        onChange={(_, isExpanded) => setBidResponseExpanded(isExpanded)}
        sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Bid Response
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BidResponseSection bidResponse={diagnostics.technical_details.bid_response} />
        </AccordionDetails>
      </Accordion>

      {/* Debug Metadata - Collapsed by default */}
      {(details.dynamo_data_source || details.sync_status?.data_source || details.venice_preview_endpoint || (details as any).generation_time_ms) && (
        <>
          <Divider />
          <Accordion
            expanded={debugMetadataExpanded}
            onChange={(_, isExpanded) => setDebugMetadataExpanded(isExpanded)}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Debug Metadata
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Data Sources */}
                {(details.dynamo_data_source || details.sync_status?.data_source) && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Data Sources
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                      Where diagnostic information was retrieved from
                    </Typography>
                    {details.dynamo_data_source && (
                      <DetailRow label="Campaign Data" value={formatDataSource(details.dynamo_data_source)} onCopy={onCopy} />
                    )}
                    {details.sync_status?.data_source && (
                      <DetailRow label="Sync Status" value={formatDataSource(details.sync_status.data_source)} onCopy={onCopy} />
                    )}
                  </Box>
                )}

                {/* Service Endpoints */}
                {details.venice_preview_endpoint && (
                  <>
                    {(details.dynamo_data_source || details.sync_status?.data_source) && <Divider />}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Service Endpoints
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                        External services used for this diagnostic check
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Ad Server (Venice):
                          </Typography>
                          <Paper 
                            variant="outlined" 
                            sx={{ 
                              p: 1, 
                              bgcolor: 'grey.50',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                mr: 1,
                              }}
                            >
                              {details.venice_preview_endpoint}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Copy URL">
                                <IconButton 
                                  size="small" 
                                  onClick={() => onCopy(details.venice_preview_endpoint!, 'Venice Endpoint')}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open in browser">
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(details.venice_preview_endpoint!, '_blank')}
                                >
                                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        </Box>
                      </Stack>
                    </Box>
                  </>
                )}

                {/* Performance - Optional */}
                {(details as any).generation_time_ms && (
                  <>
                    {(details.dynamo_data_source || details.sync_status?.data_source || details.venice_preview_endpoint) && <Divider />}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Performance
                      </Typography>
                      <DetailRow label="Total Diagnostic Time" value={`${(details as any).generation_time_ms}ms`} onCopy={onCopy} />
                      {(details as any).venice_response_time_ms && (
                        <DetailRow label="Venice Response Time" value={`${(details as any).venice_response_time_ms}ms`} onCopy={onCopy} />
                      )}
                      {(details as any).dynamo_query_time_ms && (
                        <DetailRow label="DynamoDB Query Time" value={`${(details as any).dynamo_query_time_ms}ms`} onCopy={onCopy} />
                      )}
                    </Box>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Stack>
  );
}

